import type { RfqHubFileRelease, RfqPackage, RfqProject, RfqSubmission, VendorFileRelease } from '../types';
import { isPrePortalPackage, isQuotePlaceholderPackage } from './timberRfqPrePortalVersion';
import { formatQuoteTotal, submissionQuoteTotal } from './rfqQuoteTotal';
import { packageCoversHubRelease } from './rfqPackageReleases';
import { isReferenceRelease } from './rfqReleasePurpose';
import {
  CNC_QUOTE_PLACEHOLDER_LABEL,
  CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL,
  isQuotePlaceholderSubmission,
} from './combinedQuoteSplit';

const PUBLISHED_FROM_STAGED_RE = /^Published from staged hub release \(.+\)$/;
const PUBLISHED_FROM_SAVED = 'Published from saved proposal version';
const CNC_PACKAGE_LABEL = 'CNC quote request';

export type VendorPackageQuoteStatus = 'quoted' | 'not_bidding' | 'none';

export type VendorPackageVersionRow = {
  pkg: RfqPackage;
  versionIndex: number;
  isLatest: boolean;
  quoteStatus: VendorPackageQuoteStatus;
  submissions: RfqSubmission[];
};

/** Group key for packages that represent the same portal version (e.g. republished emails). */
export function packageVersionDedupeKey(pkg: RfqPackage): string {
  if (isPrePortalPackage(pkg)) return 'pre-portal';
  const hub = (pkg.hubFileReleaseId || '').trim();
  if (hub) return `hub:${hub}`;
  const vi = typeof pkg.releaseVersionIndex === 'number' ? pkg.releaseVersionIndex : -1;
  return `vi:${vi}`;
}

function pickCanonicalPackage(
  group: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): RfqPackage {
  const withSubs = group.filter(
    p => p.id && (submissionsByPackage.get(p.id)?.length ?? 0) > 0,
  );
  const pool = withSubs.length ? withSubs : group;
  return pool.slice().sort((a, b) => {
    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return ta - tb;
  })[0]!;
}

function mergedSubmissionsForPackages(
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): RfqSubmission[] {
  const all: RfqSubmission[] = [];
  for (const pkg of packages) {
    if (!pkg.id) continue;
    all.push(...(submissionsByPackage.get(pkg.id) || []));
  }
  return all.sort((a, b) => {
    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return tb - ta;
  });
}

export function packageQuoteStatus(subs: RfqSubmission[]): VendorPackageQuoteStatus {
  if (subs.some((s) => s.notBidding)) return 'not_bidding';
  if (subs.some((s) => !!s.storagePath || !!s.cncQuote)) return 'quoted';
  return 'none';
}

/** Assign display version numbers to a supplier's packages for one RFQ project. */
export function buildVendorPackageVersions(
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
): VendorPackageVersionRow[] {
  const groups = new Map<string, RfqPackage[]>();
  for (const pkg of packages) {
    const key = packageVersionDedupeKey(pkg);
    const list = groups.get(key) || [];
    list.push(pkg);
    groups.set(key, list);
  }

  const canonicalPackages = Array.from(groups.values()).map(group =>
    pickCanonicalPackage(group, submissionsByPackage),
  );

  const sorted = canonicalPackages.slice().sort((a, b) => {
    const va = typeof a.releaseVersionIndex === 'number' ? a.releaseVersionIndex : 0;
    const vb = typeof b.releaseVersionIndex === 'number' ? b.releaseVersionIndex : 0;
    if (va !== vb) return vb - va;
    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return tb - ta;
  });

  return sorted.map((pkg, i) => {
    const group = groups.get(packageVersionDedupeKey(pkg)) || [pkg];
    const stored = pkg.releaseVersionIndex;
    const versionIndex =
      typeof stored === 'number'
        ? stored
        : isPrePortalPackage(pkg)
          ? 0
          : i + 1;
    const subs = mergedSubmissionsForPackages(group, submissionsByPackage);
    return {
      pkg,
      versionIndex,
      isLatest: false,
      quoteStatus: packageQuoteStatus(subs),
      submissions: subs,
    };
  }).map((row, _i, rows) => {
    const positiveVersions = rows.filter(r => r.versionIndex > 0).map(r => r.versionIndex);
    const latestVersion = positiveVersions.length
      ? Math.max(...positiveVersions)
      : rows.some(r => r.versionIndex === 0)
        ? 0
        : Math.max(...rows.map(r => r.versionIndex), 0);
    return {
      ...row,
      isLatest: row.versionIndex === latestVersion,
    };
  });
}

/** Mute older package versions when a newer version exists. */
export function shouldDeemphasizeRfqVersion(
  row: Pick<VendorPackageVersionRow, 'isLatest'>,
  versions: Pick<VendorPackageVersionRow, 'isLatest'>[],
): boolean {
  return versions.length > 1 && !row.isLatest;
}

/** Resolve sidebar/tree selection when the stored package id was merged as a duplicate row. */
export function resolveVendorVersionRow(
  versions: VendorPackageVersionRow[],
  selectedPackageId: string | null,
  packages: RfqPackage[],
): VendorPackageVersionRow | undefined {
  if (!versions.length) return undefined;
  if (selectedPackageId) {
    const direct = versions.find(v => v.pkg.id === selectedPackageId);
    if (direct) return direct;
    const selectedPkg = packages.find(p => p.id === selectedPackageId);
    if (selectedPkg) {
      const key = packageVersionDedupeKey(selectedPkg);
      const matched = versions.find(v => packageVersionDedupeKey(v.pkg) === key);
      if (matched) return matched;
    }
  }
  return versions.find(v => v.isLatest) ?? versions[0];
}

export function vendorPackageVersionLabel(
  versionIndex: number,
  options?: { quotePlaceholder?: boolean },
): string {
  if (versionIndex === 0) {
    if (options?.quotePlaceholder) return 'v0';
    return 'v0 (Pre-portal)';
  }
  return `v${versionIndex}`;
}

/** Version chip label that distinguishes quote placeholders from legacy pre-portal. */
export function vendorPackageVersionLabelForPackage(pkg: RfqPackage): string {
  const stored = typeof pkg.releaseVersionIndex === 'number' ? pkg.releaseVersionIndex : 0;
  return vendorPackageVersionLabel(stored, {
    quotePlaceholder: isQuotePlaceholderPackage(pkg),
  });
}

export type VendorPackageVersionNameContext = {
  hubReleaseById?: Map<string, RfqHubFileRelease>;
  vendorReleases?: VendorFileRelease[];
  orgId?: string;
};

/** Human-readable version name (staged release label) when available. */
export function vendorPackageVersionName(
  pkg: RfqPackage,
  context?: VendorPackageVersionNameContext,
): string | undefined {
  const stored = (pkg.releaseDisplayLabel || '').trim();
  if (stored) return stored;
  const placeholderName = quotePlaceholderVersionName(pkg);
  if (placeholderName) return placeholderName;

  const hubId = (pkg.hubFileReleaseId || '').trim();
  if (hubId && context?.hubReleaseById) {
    const hub = context.hubReleaseById.get(hubId);
    const hubLabel = (hub?.displayLabel || hub?.fileName || '').trim();
    if (hubLabel) return hubLabel;
  }

  if (context?.vendorReleases?.length && context.orgId) {
    const packageReleaseIds = pkg.cncPackagePayload?.releasedFileIds;
    const filtered = context.vendorReleases.filter(r => {
      if (r.supplierOrgId !== context.orgId) return false;
      if (packageReleaseIds?.length) {
        return Boolean(r.id && packageReleaseIds.includes(r.id));
      }
      return true;
    });
    const cadwork = packageReleaseIds?.length
      ? packageReleaseIds
          .map(id => filtered.find(r => r.id === id))
          .filter((r): r is VendorFileRelease => Boolean(r))
      : filtered;
    const cadworkLabel = (cadwork[0]?.displayLabel || cadwork[0]?.fileName || '').trim();
    if (cadworkLabel) return cadworkLabel;
  }

  const label = (pkg.label || '').trim();
  if (!label || label === CNC_PACKAGE_LABEL || label === PUBLISHED_FROM_SAVED) return undefined;
  if (PUBLISHED_FROM_STAGED_RE.test(label) || label.startsWith('Published from ')) return undefined;
  return label;
}

/** True when the supplier has been sent a package but has not quoted the latest version. */
export function projectNeedsQuoteResponse(
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
  projectStatus?: RfqProject['status'],
): boolean {
  if (projectStatus === 'closed') return false;
  // Reference releases are shared for production, never priced — they do not
  // create a "needs quote" state on their own.
  const quotable = packages.filter((pkg) => !isReferenceRelease(pkg));
  if (!quotable.length) return false;
  const versions = buildVendorPackageVersions(quotable, submissionsByPackage);
  const latest = versions.find((v) => v.isLatest) ?? versions[0];
  return latest?.quoteStatus === 'none';
}

export function vendorPackageQuoteStatusLabel(status: VendorPackageQuoteStatus): string {
  switch (status) {
    case 'quoted':
      return 'Quoted';
    case 'not_bidding':
      return 'Not bidding';
    default:
      return 'Awaiting quote';
  }
}

/** Primary heading for the selected package in the vendor portal main panel. */
export function vendorPackageMainHeading(row: VendorPackageVersionRow): string {
  const latest = row.submissions[0];
  if (!latest) return vendorPackageQuoteStatusLabel(row.quoteStatus);
  if (latest.notBidding) return 'Not bidding';
  // Suppliers must not see the staff CNC carve-out on quote-placeholder rows.
  if (
    isQuotePlaceholderPackage(row.pkg)
    || isQuotePlaceholderSubmission(latest)
  ) {
    return CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL;
  }
  const total = submissionQuoteTotal(latest);
  if (total != null && total > 0) return formatQuoteTotal(total);
  return vendorPackageQuoteStatusLabel(row.quoteStatus);
}

export function vendorPackageMainHeadingIsPrice(row: VendorPackageVersionRow): boolean {
  const latest = row.submissions[0];
  if (!latest || latest.notBidding) return false;
  if (isQuotePlaceholderPackage(row.pkg) || isQuotePlaceholderSubmission(latest)) {
    return false;
  }
  const total = submissionQuoteTotal(latest);
  return total != null && total > 0;
}

/** Fallback display name when releaseDisplayLabel was not stored on a placeholder package. */
export function quotePlaceholderVersionName(pkg: RfqPackage): string | undefined {
  if (!isQuotePlaceholderPackage(pkg)) return undefined;
  const stored = (pkg.releaseDisplayLabel || '').trim();
  return stored || CNC_QUOTE_PLACEHOLDER_LABEL;
}

export function nextVendorPackageVersionIndex(
  rfqProjectId: string,
  existingPackages: RfqPackage[],
): number {
  const forProject = existingPackages.filter(
    p => p.rfqProjectId === rfqProjectId && !isPrePortalPackage(p),
  );
  if (!forProject.length) return 1;
  const maxStored = forProject.reduce(
    (max, p) => Math.max(max, typeof p.releaseVersionIndex === 'number' ? p.releaseVersionIndex : 0),
    0,
  );
  return Math.max(maxStored, 0) + 1;
}

/** True when every target org already has an RFQ package for this staged hub release. */
export function hubReleaseAlreadyPublishedForTargets(
  packages: RfqPackage[],
  rfqProjectId: string,
  hubFileReleaseId: string,
  targetOrgIds: string[],
): boolean {
  const hub = hubFileReleaseId.trim();
  if (!hub || !targetOrgIds.length) return false;
  return targetOrgIds.every(orgId =>
    packages.some(
      p =>
        p.rfqProjectId === rfqProjectId
        && p.supplierOrgId === orgId
        && packageCoversHubRelease(p, hub),
    ),
  );
}

/** Org ids that do not yet have a package for this staged hub release. */
export function filterTargetsWithoutHubReleasePackage(
  packages: RfqPackage[],
  rfqProjectId: string,
  hubFileReleaseId: string,
  targetOrgIds: string[],
): string[] {
  const hub = hubFileReleaseId.trim();
  if (!hub) return targetOrgIds;
  return targetOrgIds.filter(
    orgId =>
      !packages.some(
        p =>
          p.rfqProjectId === rfqProjectId
          && p.supplierOrgId === orgId
          && packageCoversHubRelease(p, hub),
      ),
  );
}

/** True when at least one roster supplier still needs a package for this release. */
export function hubReleaseHasPendingPublishTargets(
  packages: RfqPackage[],
  rfqProjectId: string,
  hubFileReleaseId: string,
  targetOrgIds: string[],
): boolean {
  return (
    filterTargetsWithoutHubReleasePackage(packages, rfqProjectId, hubFileReleaseId, targetOrgIds).length
    > 0
  );
}
